import { useEffect, useRef, useState } from "react";
import Image from "@Components/Image";
import Reveal from "@Components/Reveal/Reveal";
import { pageAsset } from "@Utils/getStaticAssetPath";
// The site's primary CTA, same class as the hero's button rather than a second pill
// styled to match — see the note in Hero.
import navStyles from "@Components/Navbar/Navbar.module.scss";
import APPLY_URL from "../../constants";
import styles from "./AmbassadorCta.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

// Images come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("AmbassadorV2");

/**
 * The five faces in the fan — real ambassadors, the same way Career's band uses
 * real teammates.
 *
 * ── Where these came from ──────────────────────────────────────────────────
 * Supplied as five 1254x1254 circular headshots, one per creator, all shot to
 * the same framing and on the same background. They replace two earlier
 * attempts, and it is worth knowing why both failed:
 *
 *   1. Instagram profile pictures. Served at 150x150 at best, which forced the
 *      whole cluster down to 50 / 54 / 73px to avoid upscaling — and at that
 *      size it read as undersized beside the heading.
 *   2. Face crops out of the reel posters. Big enough (150-320px), but they are
 *      video stills: every poster carries burned-in caption text that caps how
 *      wide each box can go, so the head-to-circle ratio could not be evened
 *      out across the five. Widening them to match pulled "Finding
 *      Accommodation as an" and "3 things" into the circles.
 *
 * These have none of those problems, so the cluster runs at Career's full
 * 84 / 90 / 123px.
 *
 * ⚠️  CROPPED TO EACH CIRCLE'S OWN BOUNDING BOX, not to the file's edges. The
 * supplied files are circles matted on black inside a square, and the circle
 * does not reach the square's edges. `border-radius: 50%` inscribes a circle in
 * whatever box it is given, so cropping to the file would leave a black rim all
 * the way round. Regenerate by finding the non-black bbox, squaring it off from
 * the centre, and resizing to 320px.
 *
 * ⚠️  RESOLUTION NO LONGER PICKS THE SLOTS. All five are 320px, which serves even
 * the 123px centre at 2x, so the order below is a composition choice and can be
 * reshuffled freely — unlike the two attempts above, where it could not.
 */
const faceLeft2 = asset("faces/dabrien3.jpg");
const faceLeft1 = asset("faces/rclangeline.jpg");
const faceCentre = asset("faces/mr_kenling.jpg");
const faceRight1 = asset("faces/itsmeameliaa._.jpg");
const faceRight2 = asset("faces/damolaa_x.jpg");

/**
 * The closing "ready to join" band.
 *
 * ⚠️  NO BACKGROUND PHOTOGRAPH, unlike the section it replaces.
 *
 * The old band laid its copy over `ready_to_join.jpg` and needed a scrim to keep the
 * text legible; at phone widths it switched to stacking the image above the copy
 * instead, which is two layouts to maintain for one section. Flat #0a0a0a says the same
 * thing at every width, matches the closing bands on the other v2 pages, and costs a
 * request less on the page's slowest section.
 *
 * The copy and the destination are the old page's — `ambassador.readytojoin.*` and the
 * same Typeform the hero opens.
 */
const Cta = () => {
  const { t } = useTranslationHook();
  const avatarsRef = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  // Reveal the avatars from the centre outward when the cluster scrolls in.
  // Deliberately the same observer-and-class approach as Career's band rather
  // than the shared `Reveal` wrapper: the fan needs five elements to ease from
  // five different start transforms on one shared trigger, which `Reveal` (one
  // element, one delay) cannot express.
  useEffect(() => {
    const node = avatarsRef.current;
    if (!node) return undefined;

    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section className={styles.section}>
      <div ref={avatarsRef} className={`${styles.avatars} ${shown ? styles.avatarsShown : ""}`}>
        <span className={`${styles.avatar} ${styles.avatarSmall} ${styles.side} ${styles.left2}`}>
          <Image src={faceLeft2} alt="" className={styles.avatarImage} width={84} height={84} />
        </span>
        <span className={`${styles.avatar} ${styles.avatarMedium} ${styles.side} ${styles.left1}`}>
          <Image src={faceLeft1} alt="" className={styles.avatarImage} width={90} height={90} />
        </span>
        <span className={`${styles.avatarCenter} ${styles.center}`}>
          <Image src={faceCentre} alt="" className={styles.avatarImage} width={123} height={123} />
        </span>
        <span className={`${styles.avatar} ${styles.avatarMedium} ${styles.side} ${styles.right1}`}>
          <Image src={faceRight1} alt="" className={styles.avatarImage} width={90} height={90} />
        </span>
        <span className={`${styles.avatar} ${styles.avatarSmall} ${styles.side} ${styles.right2}`}>
          <Image src={faceRight2} alt="" className={styles.avatarImage} width={84} height={84} />
        </span>
      </div>

      {/* ⚠️  THREE SEPARATE `Reveal`s WITH CAREER'S OWN DELAYS (450 / 560 / 660),
          not one wrapper around all of it. The fan finishes its centre-outward
          reveal at about 300ms, and these pick up from there so the band reads
          as one continuous entrance. Wrapping the three in a single `Reveal`
          brought them in together and lost that hand-off. */}
      <div className={styles.copy}>
        <Reveal as="h2" className={styles.heading} delay={450}>
          {t("ambassador.readytojoin.title")}
        </Reveal>
        <Reveal as="p" className={styles.subtitle} delay={560}>
          {t("ambassador.readytojoin.subtitle")}
        </Reveal>
      </div>

      <Reveal delay={660}>
        <a
          className={navStyles.tryButton}
          href={APPLY_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="ambassador-v2-apply-footer"
        >
          {t("ambassador.readytojoin.cta")}
        </a>
      </Reveal>
    </section>
  );
};

export default wrapperHOC(Cta, {
  componentName: "Cta-AmbassadorV2",
  showForChina: true,
});
